import React, { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { useEmpresasContext } from '../context/EmpresasContext';
import { AsignacionesUsuarioMaquinaService } from '../services/asignacionesUsuarioMaquinaService';

// ===============================
// TIPOS
// ===============================

export interface FiltroMaquinasResult {
  maquinasPermitidas: number[] | null; // null = todas, array = IDs específicos
  isLoading: boolean;
  error: string | null;
  tieneAccesoCompleto: boolean; // true si es supervisor/admin
}

// ===============================
// HOOK PRINCIPAL
// ===============================

export function useFiltroMaquinasUsuario() {
  const [maquinasPermitidas, setMaquinasPermitidas] = useState<number[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { usuario } = useAuth();
  const { empresaActiva } = useEmpresasContext();

  const tieneAccesoCompleto = usuario?.tipo_usuario === 'supervisor' || usuario?.tipo_usuario === 'administrador';

  useEffect(() => {
    const cargarFiltroMaquinas = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!usuario || !empresaActiva?.empresa?.id) {
          setMaquinasPermitidas(null);
          return;
        }

        // Si es supervisor o administrador: acceso completo
        if (tieneAccesoCompleto) {
          setMaquinasPermitidas(null); // null = todas las máquinas
          return;
        }

        // Si es operador: solo máquinas asignadas
        const maquinasAsignadas = await AsignacionesUsuarioMaquinaService.obtenerMisMaquinasAsignadas();
        const idsPermitidos = maquinasAsignadas.map(m => m.id);
        
        setMaquinasPermitidas(idsPermitidos);
      } catch (err: any) {
        console.error('Error cargando filtro de máquinas:', err);
        setError(err.message || 'Error cargando permisos de máquinas');
        
        // En caso de error, permitir acceso completo para no bloquear
        setMaquinasPermitidas(null);
        
      } finally {
        setIsLoading(false);
      }
    };

    cargarFiltroMaquinas();
  }, [usuario?.id, empresaActiva?.empresa?.id, tieneAccesoCompleto]);

  // Función para verificar si una máquina específica está permitida
  const tieneMaquinaPermitida = (maquinaId: number): boolean => {
    // Si tiene acceso completo o maquinasPermitidas es null = todas permitidas
    if (tieneAccesoCompleto || maquinasPermitidas === null) {
      return true;
    }
    
    // Si es operador, verificar si la máquina está en su lista
    return maquinasPermitidas.includes(maquinaId);
  };

  // Función para filtrar una lista de máquinas
  const filtrarMaquinas = <T extends { id: number }>(maquinas: T[]): T[] => {
    // Si tiene acceso completo, retornar todas
    if (tieneAccesoCompleto || maquinasPermitidas === null) {
      return maquinas;
    }
    
    // Si es operador, filtrar por máquinas asignadas
    return maquinas.filter(maquina => maquinasPermitidas.includes(maquina.id));
  };

  return {
    maquinasPermitidas,
    isLoading,
    error,
    tieneAccesoCompleto,
    tieneMaquinaPermitida,
    filtrarMaquinas
  };
}

// ===============================
// HOOK PARA VERIFICAR MÁQUINA ESPECÍFICA
// ===============================

export function useVerificarAccesoMaquina(maquinaId: number | null) {
  const [tieneAcceso, setTieneAcceso] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { usuario } = useAuth();
  const { empresaActiva } = useEmpresasContext();

  const tieneAccesoCompleto = usuario?.tipo_usuario === 'supervisor' || usuario?.tipo_usuario === 'administrador';

  useEffect(() => {
    const verificarAcceso = async () => {
      try {
        setIsLoading(true);

        if (!maquinaId || !usuario || !empresaActiva?.empresa?.id) {
          setTieneAcceso(null);
          return;
        }

        // Si es supervisor o administrador: acceso completo
        if (tieneAccesoCompleto) {
          setTieneAcceso(true);
          return;
        }

        // Si es operador: verificar acceso específico
        const verificacion = await AsignacionesUsuarioMaquinaService.verificarAccesoMaquina(maquinaId);
        setTieneAcceso(verificacion.tiene_acceso);
        
      } catch (error) {
        console.error('Error verificando acceso a máquina:', error);
        // En caso de error, denegar acceso por seguridad
        setTieneAcceso(false);
      } finally {
        setIsLoading(false);
      }
    };

    verificarAcceso();
  }, [maquinaId, usuario?.id, empresaActiva?.empresa?.id, tieneAccesoCompleto]);

  return {
    tieneAcceso,
    isLoading,
    tieneAccesoCompleto
  };
}

// ===============================
// UTILIDADES
// ===============================

/**
 * Wrapper para componentes que requieren verificación de acceso a máquina
 */
export function withAccesoMaquina<P extends { maquinaId: number }>(
  Component: React.ComponentType<P>
) {
  return function ComponenteConAcceso(props: P) {
    const { tieneAcceso, isLoading } = useVerificarAccesoMaquina(props.maquinaId);

    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!tieneAcceso) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <div className="text-yellow-600 mb-2">
            <svg className="mx-auto h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m3-7V6a3 3 0 00-6 0v3m8 0a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6a2 2 0 012-2m8 0V9a3 3 0 00-3-3H9a3 3 0 00-3 3v3" />
            </svg>
          </div>
          <p className="text-sm text-yellow-800">
            No tienes acceso a esta máquina
          </p>
        </div>
      );
    }

    return <Component {...props} />;
  };
}
